import axios from 'axios';
import { API_BASE_URL } from '../config';
const TOKEN_KEY = 'deviceFleetToken';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Bearer token and tenant ID to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = localStorage.getItem('selectedTenantId');
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/google')) {
      localStorage.removeItem(TOKEN_KEY);
      window.dispatchEvent(new CustomEvent('deviceFleetAuthExpired'));
    }
    return Promise.reject(error);
  }
);

// Device API
export const deviceAPI = {
  getAll: () => api.get('/devices'),
  getById: (id) => api.get(`/devices/${encodeURIComponent(id)}`),
  create: (data) => api.post('/devices', data),
  update: (id, data) => api.put(`/devices/${encodeURIComponent(id)}`, data),
  delete: (id) => api.delete(`/devices/${encodeURIComponent(id)}`),
  search: (query) => api.get(`/devices/search/${encodeURIComponent(query)}`),
  bulkImport: (devices) => api.post('/devices/bulk-import', { devices }),
  getEditHistory: (id) => api.get(`/devices/${encodeURIComponent(id)}/edit-history`),
  getAllRecentEdits: (limit = 1000) => api.get(`/devices/edits/recent?limit=${limit}`),
};

// Employee API
export const employeeAPI = {
  getAll: () => api.get('/employees'),
  getById: (id) => api.get(`/employees/${encodeURIComponent(id)}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${encodeURIComponent(id)}`, data),
  delete: (id) => api.delete(`/employees/${encodeURIComponent(id)}`),
  bulkUpload: (formData) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const tenantId = localStorage.getItem('selectedTenantId');
    return axios.post(`${API_BASE_URL}/employees/bulk-upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      },
    });
  },
};

// Assignment API
export const assignmentAPI = {
  getAll: () => api.get('/assignments'),
  getById: (id) => api.get(`/assignments/${encodeURIComponent(id)}`),
  create: (data) => api.post('/assignments', data),
  update: (id, data) => api.put(`/assignments/${encodeURIComponent(id)}`, data),
  delete: (id) => api.delete(`/assignments/${encodeURIComponent(id)}`),
  getByEmployee: (employeeId) => api.get(`/assignments/employee/${encodeURIComponent(employeeId)}`),
  getByDevice: (deviceId) => api.get(`/assignments/device/${encodeURIComponent(deviceId)}`),
  getDeviceHistory: (deviceId) => api.get(`/assignments/device/${encodeURIComponent(deviceId)}/history`),
};

// Statistics API
export const statsAPI = {
  getAll: () => api.get('/stats'),
  getByModel: () => api.get('/stats/by-model'),
  getUsage: () => api.get('/stats/usage'),
  getEmployeesWithMultipleDevices: () => api.get('/stats/employees-multiple-devices'),
};

// Auth API (Google sign-in is public — no token required)
export const authAPI = {
  googleLogin: (body) =>
    axios.post(`${API_BASE_URL}/auth/google`, body, {
      headers: { 'Content-Type': 'application/json' },
    }),
  getConfig: () =>
    axios.get(`${API_BASE_URL}/auth/config`, {
      headers: { 'Content-Type': 'application/json' },
    }),
  me: () => api.get('/auth/me'),
};

// Settings API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  getDeviceTypes: () => api.get('/settings/device-types'),
  addDeviceType: (type) => api.post('/settings/device-types', { type }),
  deleteDeviceType: (type) => api.delete(`/settings/device-types/${encodeURIComponent(type)}`),
  getDeliveryUnits: () => api.get('/settings/delivery-units'),
  addDeliveryUnit: (unit) => api.post('/settings/delivery-units', { unit }),
  deleteDeliveryUnit: (unit) => api.delete(`/settings/delivery-units/${encodeURIComponent(unit)}`),
  getRegisteredTeamIds: () => api.get('/settings/registered-team-ids'),
  addRegisteredTeamId: (teamId) => api.post('/settings/registered-team-ids', { teamId }),
  deleteRegisteredTeamId: (teamId) => api.delete(`/settings/registered-team-ids/${encodeURIComponent(teamId)}`),
};

// Tenant API
export const tenantAPI = {
  getAll: () => api.get('/tenants'),
  getById: (id) => api.get(`/tenants/${encodeURIComponent(id)}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${encodeURIComponent(id)}`, data),
  delete: (id) => api.delete(`/tenants/${encodeURIComponent(id)}`),
};

// Users / Device Managers API
export const usersAPI = {
  getDeviceManagers: () => api.get('/users/device-managers'),
  addDeviceManager: (data) => api.post('/users/device-managers', data),
  updateDeviceManager: (id, data) => api.put(`/users/device-managers/${encodeURIComponent(id)}`, data),
  deactivateUser: (id) => api.patch(`/users/device-managers/${encodeURIComponent(id)}/deactivate`),
  deleteUser: (id) => api.delete(`/users/device-managers/${encodeURIComponent(id)}`),
  sendAssignmentEmails: (data) => api.post('/users/send-assignment-emails', data),
};

// Transfer API
export const transferAPI = {
  requestTransfer: (data) => api.post('/transfers/request', data),
  getMyTransfers: () => api.get('/transfers/my-transfers'),
  getTransferCount: () => api.get('/transfers/count'),
  approveTransfer: (id) => api.post(`/transfers/${encodeURIComponent(id)}/approve`),
  rejectTransfer: (id) => api.post(`/transfers/${encodeURIComponent(id)}/reject`),
};

// Device Request API
export const deviceRequestAPI = {
  create: (data) => api.post('/device-requests', data),
  getAll: () => api.get('/device-requests'),
  getMyRequests: () => api.get('/device-requests/my-requests'),
  getRequestCount: () => api.get('/device-requests/count'),
  updateStatus: (id, data) => api.put(`/device-requests/${encodeURIComponent(id)}/status`, data),
  delete: (id) => api.delete(`/device-requests/${encodeURIComponent(id)}`),
};

// Email Scheduler API
export const schedulerAPI = {
  getStatus: () => api.get('/scheduler/status'),
  triggerManual: () => api.post('/scheduler/trigger'),
  updateDelay: (delayInSeconds) => api.put('/scheduler/email-delay', { delayInSeconds }),
  stop: () => api.post('/scheduler/stop'),
  start: (cronSchedule) => api.post('/scheduler/start', { cronSchedule }),
};

export default api;
